  <template>
  <div id="calc" class="calculator wrapper container-fluid" @click.stop.prevent="handleClick">
    <p class="mb--1 mb-1">
      This decision support tool is based on the algorithm in 
      <a href="https://www.sign.ac.uk/media/1766/sign-160-uti-0-1_web-version.pdf">SIGN 160: Management of suspected bacterial lower urinary tract infection in adult women</a>, 
      for diagnostic and management options for women under 65 years presenting with suspected LUTI.  
      It should be used <strong>only for women who are not pregnant</strong>. 
      Please confirm that the patient is not pregnant before proceeding.
    </p>
    <p class="mb--2 mb-2">This decision support tool is <em>not</em> suitable for patient with features suggestive of Upper UTI such as fever, vomiting or flank pain.</p>
    <div class="tabs">
      <nav class="tabs__nav">
        <button v-for="(tab, index) in tabs" v-bind:key="tab.title" :id="'tab-button-'+index" role="button" class="tabs__nav-item txt--strong" :class="{ 'tabs__nav-item--active bg--primary': tab.display }" v-on:click="selectTab(tab)">
          <span v-html="tab.title"></span>
          <i v-if="!tab.display" aria-hidden="true" class="fa fa-chevron-down ml-1 ml--1 show--md"></i>
          <i v-if="tab.display" aria-hidden="true" class="fa fa-chevron-up ml-1 ml--1 show--md"></i>	
        </button>
      </nav>
      <div class="tabs__content" id="tabcontent">
        <div v-for="(tab, index) in tabs" v-bind:key="tab.content">
          <div v-if="tab.display" :id="'tab-content-'+index" class="bdr--light-gray p--1 wysiwyg" role="tabpanel" :aria-labelledby="tab.title" v-html="tab.content"></div>
        </div>
      </div>
    </div>

    <div class="questions__intro-text">
      <div class="row">
        <div class="col-12 col-xs-12">
          <p>Click on the answer to each question below, then click on Recommendations.</p>
        </div>
      </div>
    </div>

    <div v-for="q in questions" :key="q.id">
      <div class="row main-question">
        <div class="col-12 col-xs-12 col-sm-6">
          <h3 class="txt--xl qa">{{q.title}}</h3>
          <p v-html="q.info"></p>
        </div>
        <div class="col-12 col-xs-12 col-sm-6">
          <div class="list-group">
            <div v-for="o in q.options" :key="o.id">
              <button @click="optionclicked(q.id,o.id)"
                  :id="q.id+'-'+o.id"
                  class="answer"
                  @keydown.enter="optionclicked(event, q.id,o.id)"
                  type="button"
                  v-bind:class="{'list-group-item list-group-item-action d-flex justify-content-between align-items-center active':(o.selected), 'list-group-item list-group-item-action d-flex justify-content-between align-items-center':(!o.selected)}">
                {{o.text}}
              </button>
            </div>
          </div>
        </div>
      </div>
      <hr class="show--sm mb-2 mb--2">
    </div>
    
    <div v-if="showAdditonalQuestions == true" class="additional-questions">
      <div v-for="q in additonalQuestions" :key="q.id">
        <div class="row add-question">
          <div class="col-12 col-xs-12 col-sm-6">
            <h3 class="txt--xl qa">{{q.title}}</h3>
            <p v-html="q.info"></p>
          </div>
          <div class="col-12 col-xs-12 col-sm-6">
            <div class="list-group">
              <div v-for="o in q.options" :key="o.id">
                <button @click="optionAdditionalclicked(q.id,o.id)"
                    :id="q.id+'-'+o.id"
                    @keydown.enter="optionAdditionalclicked(event, q.id,o.id)"
                    class="answer"
                    type="button"
                    v-bind:class="{'list-group-item list-group-item-action d-flex justify-content-between align-items-center active':(o.selected), 'list-group-item list-group-item-action d-flex justify-content-between align-items-center':(!o.selected)}">
                  {{o.text}}
                </button>
              </div>
            </div>
          </div>
        </div>
        <hr class="show--sm mb-2 mb--2">
      </div>
    </div>
        
        
      <div id="error"  v-if="errormessage.length > 0" class="alert alert-info alert--info alert-informative mb-3 mb--3" role="alert" v-html="errormessage"></div>

      <div class="calculator__result bg-secondary bg--secondary p--2 p-2 mb--2 mb-2">
        <!-- Calculator Copy Section -->
        <div id="calculator-copy" class="color--white text-white mb-1 mb--1" v-if="displayresult">
          <div id="result-response" v-if="isvalid && isAdditionalvalid"></div>
          <p id="error-header" v-if="errormessage.length > 0"  class="h4 txt--lg"><strong>Recommendation</strong> : N/A</p>
          <p id="recommendation" class="txt--xl h3" v-if="result.text.length > 0 && errormessage.length == 0"><strong>Recommendation:</strong> {{result.text}}</p>
          <div id="subtext" v-if="result.calctext.length > 0 && errormessage.length == 0" v-html="result.subtext"></div>
          <p id="answered-questions" v-if="result.calctext.length > 0">
            <span v-html="result.calctext"></span>
            <span v-if="result.vagtext.length > 0 && showAdditonalQuestions" v-html="result.vagtext"></span>
          </p>
        </div>
        <!-- Calculator Controls -->
        <div class="control-holder">
          <button id="calculate" class="btn btn-primary mr-1 mr--1 mb-1" @click="calculateclicked()" type="button" v-if="!displayresult">Recommendations</button>
          <button id="reset" class="btn btn-primary mr-1 mr--1 mb-2" type="button" @click="reset()" v-if="displayresult">Reset</button>
          <!-- PLEASE DO NO DELETE --> 
          <!-- SIGN GUIDELINES -->
          <!-- <div @click.stop.prevent="handleClick">
            <a id="showDipstick" class="btn btn-primary mr-1 mb-2" data-id="4200" href="/management-of-suspected-bacterial-lower-urinary-tract-infection-luti-in-adult-women/under-65-years/decision-support/dipstick-test-luti-in-women-65-years/" v-if="showDipstick">Open Dipstick Test</a>
          </div> -->
          <!-- GP TOOLKIT -->
          <!-- <div @click.stop.prevent="handleClick">
            <a id="showDipstick" class="btn btn-primary mr-1 mb-2" data-id="3511" href="/management-of-suspected-bacterial-lower-urinary-tract-infection-luti-in-adult-women/sign-luti-65/dipstick-test-luti-in-women-65-years/" v-if="showDipstick">Open Dipstick Test</a>
          </div> -->
          <button id="copyButton" class="btn btn-primary mr-1 mr--1 mb-1" type="button" @click="copyToClipboard()" v-if="displayresult && errormessage.length == 0">Copy Result</button>
          <!--<button id="emailPdf" class="btn mr-1 mr--1 hide--quris" type="button" @click="displayForm" v-if="displayresult">Email PDF</button>-->
        </div>
      </div>
        <!--
        <div class="bg--offwhite bg-offwhite p-2 p--2" v-if="displayresult && showform">
            <p class="color--black">For security reasons, this form is only available within the NHS network. You can use it to email to an NHS email a PDF with the decision support result for this patient and then save it to the patient record.</p>
            <div class="color--black alert alert-danger alert--error mb-1 mb--1" role="alert" v-if="emailmessage.length > 0" v-html="emailmessage"> </div>
            <div class="form__group mb--2">
              <label for="emailaddresses" class="form__label mb--1 txt--strong">Email addresses:</label>
              <input v-model="emailaddresses" type="text" class="form__input" name="emailaddresses" id="emailaddresses" placeholder="Enter email address(es) to receive this decision support output. NHS emails only">
            </div>
            <div class="form__group mb--2">
              <label for="patientname" class="form__label mb--1 txt--strong">Patient's Full Name:</label>
              <input v-model="patientname" type="text" class="form__input" name="patientname" id="patientname" placeholder="Enter patient’s full name">
            </div>
            <div class="form__group mb--2">
              <label for="patientchinumber" class="form__label mb--1 txt--strong">Patient CHI Number:</label>
              <input v-model="patientchinumber" type="text" class="form__input" name="patientchinumber" id="patientchinumber" placeholder="Enter patient’s CHI number">
            </div>
            <button class="btn mr-1 mr--1" type="button" @click="sendToPDF">Send</button>
          </div>
          <div class="alert alert-success alert--success mb-3 mb--3" role="alert" v-if="displayresult && !showform && emailmessage.length > 0 && emailaddresses.length > 0 && patientname.length > 0 && patientchinumber.length > 0" v-html="emailmessage"></div> -->
        
    <Content />
  </div>
  </template>

  <script>
  import Content from '@/components/Content';
  import "isomorphic-fetch";

  export default {
    name: "Calculator",
    components: {
      Content
    },
  data() {
    return {
      questionsAnswered: [null,null,null,null,null],
      questions: [
        {
          id: "q1",
          title: "1. Dysuria (pain on urinating)",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        },        
        {
          id: "q2",
          title: "2. Increased frequency of urination",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        },        
        {
          id: "q3",
          title: "3. Urgency of need for urination",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        },        
        {
          id: "q4",
          title: "4. Visible haematuria (blood in urine)",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        },        
        {
          id: "q5",
          title: "5. Nocturia (having to urinate more often at night)",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        }
      ],
      additonalQuestions: [
        {
          id: "q6",
          title: "6. New onset vaginal irritation",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        },        
        {
          id: "q7",
          title: "7. New onset vaginal discharge",
          required: true,
          options: [
            {
              id: "o1",
              text: "Yes",
              value: 1,
              selected: false
            },
            {
              id: "o2",
              text: "No",
              value: 0,
              selected: false
            }
          ]
        }
      ],
      additionalQuestionsAnswered: [null,null],
      showAdditonalQuestions: false,
      result: {
        score: 0,
        additionalScore: 0,
        calctext: "",
        vagtext: "",
        text: "",
        subtext: ""
      },
      displayresult: false,
      showform: false,
      showDipstick: false,
      errormessage: "",
      emailmessage: "",
      isvalid: false,
      isAdditionalvalid: true,
      answered_count: 0,
      add_answered_count: 0,
      tabs: [
        {
          title: "When to use",
          display: false,
          accordion: false,
          content: `
            <p>Use for adult women less than 65 years who are not pregnant. </p>
            <p>Please confirm that the patient is not pregnant before proceeding. </p>
          `,
        },
        {
          title: "Evidence",
          display: false,
          accordion: false,
          content: `
            <p>This decision support tool is based on <a href="https://www.sign.ac.uk/media/1766/sign-160-uti-0-1_web-version.pdf" target="_blank">SIGN guideline 160 (2020): Management of suspected bacterial lower urinary tract infection in adult women.</a></p>
          `,
        },
        {
          title: "Calculation<span class='show--md'> & interpretation</span>",
          display: false,
          accordion: false,
          content: `
            <h4 class="txt--lg">Diagnosis based on symptoms</h4>
            <p><strong>If only one of the following symptoms</strong> is present - dysuria, frequency, urgency, visible haematuria, nocturia – then self-care advice is recommended. </p>
            <p><strong>If two or more of the following symptoms</strong> is present - dysuria, frequency, urgency, visible haematuria, nocturia – then dipstick test and self-care advice is recommended. </p>
            <p><strong>If one or more of the following symptoms</strong> is present - dysuria, frequency, urgency, visible haematuria, nocturia – <strong>AND new onset</strong> vaginal irritation or discharge – then it is necessary to rule out other conditions that present in similar ways to uncomplicated UTI.</p>
            <h4 class="txt--lg">Diagnosis based on dipstick test result</h4>
            <p><strong>If the dipstick test is negative</strong> provide self-care advice and consider sending a urine specimen for culture to inform the diagnosis.</p>
            <p><strong>If the dipstick test is positive</strong> provide self-care advice and consider NSAIDs. Where antibiotics are required, prescribe a 3-day course. It may also be necessary to send a urine specimen for culture if the patient: </p>
            <ul>
              <li>Has a history of resistant urinary isolates</li>
              <li>Has taken any antibiotics in the past 6 months</li>
              <li>Fails to respond to empirical antibiotics</li>
            </ul>
          `,
        },
        {
          title: "Practice<span class='show--md'> considerations</span>",
          display: false,
          accordion: false,
          content: `
            <p>This decision support tool is only relevant to non-pregnant women under 65.</p>
          `,
        }
      ]
    };
  },
  methods: {				
    handleClick(e) {
      if (e.target.matches("a")) {
        let url = e.target.href;
        let target = e.target.target == "_blank" ? "_blank" : "_self";
        let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
        console.log(dataId,'DATAID');
        var isnativeapp = document.getElementById("isnativeapp");
        if (isnativeapp === undefined || isnativeapp === null) 
        {
            window.open(url,target);  
        }
        else{
            let data = {title:'',nodeid: dataId, href:url, target: target};
            window.Native('dataCallback', data);
        }	
      }
    },
    selectTab(selectedTab) {
      this.tabs.forEach(tab => {
        if(tab.display) {
          tab.display = false;
        } else {
          tab.display = (tab.title == selectedTab.title);
        }
      });
    },
    displayForm(){		
      this.showform = !this.showform;
    },
    reset() {
      this.result.score = 0;
      this.result.additionalScore = 0;
      this.result.calctext = "";
      this.result.text = "";
      this.displayresult = false;
      this.showDipstick = false;
      this.showform = false;
      this.errormessage= "";
      this.emailmessage = "";
      this.isvalid= false;
      this.answered_count= 0;
      this.showAdditonalQuestions = false;
      this.isAdditionalvalid = true;
      this.add_answered_count = 0;
      this.totalYesAnswered(this.questionsAnswered);
      this.totalYesAnswered(this.additionalQuestionsAnswered);
      this.questions.forEach(q => {
        q.options.forEach(o => {
            o.selected = false;
          });
      });
      this.additonalQuestions.forEach(q => {
        q.options.forEach(o => {
            o.selected = false;
          });
      });
      window.scrollTo(0, 0);
    },
    displayError() {
      this.errormessage =	"<h2 class='txt--lg'>Missing input</h2>";
      this.errormessage += "<p>Please answer all of questions displayed in order to see the Recommendations.</p>";
      setTimeout(()=> {
        document.getElementById("error").scrollIntoView(true)},100);
    },

    calculateclicked() {
      this.showDipstick = false;
      this.showform = false;
      this.displayresult = true;
      if(this.showAdditonalQuestions){
        if (this.add_answered_count == this.additonalQuestions.length && this.answered_count == this.questions.length) {
          this.errormessage = "";
        } else {
          this.displayError();
        }
      } else {
        if (this.answered_count < this.questions.length) {
          this.displayError();
        } else {
          this.errormessage = "";
        }
      }
      if (this.totalYesAnswered(this.questionsAnswered) == 0) {
        this.result.text = "Advise that urinary tract infection is unlikely";
        this.result.subtext = `
          <p><strong>Advise the patient that it is unlikely that they have a urinary tract infection.</strong></p>
          <p>Clinicians should also refer to local guidance where applicable. </p>
        `;
      }
      else if (this.totalYesAnswered(this.questionsAnswered) === 1 && this.totalYesAnswered(this.additionalQuestionsAnswered) == 0) {
        this.result.text = "Provide self-care advice";
        this.result.subtext = `
          <p><strong>Advise the patient that a UTI cannot be confirmed based on a single urinary symptom and to return if the symptom fails to improve or worsens.</strong></p>
          <p><strong>Provide self-care advice on the following issues:</strong></p>
          <ul>
            <li>Hydration</li>
            <li>Symptomatic relief (paracetamol, ibuprofen) </li>
            <li>Voiding behaviour</li>
            <li>Personal hygiene</li>
          </ul>
          <p>Consider the following points in the <a href="https://www.sign.ac.uk/media/1766/sign-160-uti-0-1_web-version.pdf" target="_blank">SIGN 160</a> checklist:</p>
          <ul>
            <li>Explain the symptoms of UTI, how to tell if a UTI might be present and when to seek medical advice, e.g. from GP or pharmacist. </li>
            <li>Inform women of the cause of UTIs and the effect UTIs have on the body.</li>
            <li>Discuss with women how having other conditions can make them more susceptible to UTIs, e.g. diabetes. Offer time to answer questions women may have.</li>
            <li>Discuss the implications of recurring UTIs on health in general, including the bladder. </li>
            <li>Provide women with the <a href="https://www.sign.ac.uk/media/1879/pat160-uti-patient-booklet.pdf" target="_blank">Patient Booklet</a> for the SIGN 160 guideline to help them understand and manage UTIs. </li>
            <li>Explain the rationale for not prescribing and delayed prescribing.</li>
          </ul>
          <p>Clinicians should also refer to local guidance where applicable.</p>
        `;
      }
      else if (this.totalYesAnswered(this.questionsAnswered) > 1 && this.totalYesAnswered(this.additionalQuestionsAnswered) == 0) {
        this.showDipstick = true;
        this.result.text = "Perform dipstick testing and provide self-care advice. Return to previous page to access the Dipstick test decision aid.";
        this.result.subtext = `
          <p><strong>1.Perform urinary dipstick test for nitrites to confirm bacteriuria.</strong></p>
          <ul>
            <li>Discuss how to provide a urine sample for dipstick testing, including advice around ensuring the bladder has not been emptied for at least four hours before taking the sample.</li>
          </ul>
          <p><strong>2. Provide self-care advice</strong></p>
          <ul class="mb-1">
            <li>Hydration</li>
            <li>Symptomatic relief (paracetamol, ibuprofen) </li>
            <li>Voiding behaviour</li>
            <li>Personal hygiene</li>
          </ul>
          <p>Consider the following points in the <a href="https://www.sign.ac.uk/media/1766/sign-160-uti-0-1_web-version.pdf" target="_blank">SIGN 160</a> checklist:</p>
          <ul>
            <li>Explain the symptoms of UTI, how to tell if a UTI might be present and when to seek medical advice, e.g. from GP or pharmacist. </li>
            <li>Inform women of the cause of UTIs and the effect UTIs have on the body.</li>
            <li>Discuss with women how having other conditions can make them more susceptible to UTIs, e.g. diabetes. Offer time to answer questions women may have.</li>
            <li>Discuss the implications of recurring UTIs on health in general, including the bladder. </li>
            <li>Provide women with the <a href="https://www.sign.ac.uk/media/1879/pat160-uti-patient-booklet.pdf" target="_blank">Patient Booklet</a> for the SIGN 160 guideline to help them understand and manage UTIs. </li>
            <li>Explain the rationale for not prescribing and delayed prescribing.</li>
          </ul>
          <p>Clinicians should also refer to local guidance where applicable.</p>
        `;
      }
      else if (this.totalYesAnswered(this.questionsAnswered) >= 1 && this.totalYesAnswered(this.additionalQuestionsAnswered) > 0) {
        this.result.text = "Rule out other causes";
        this.result.subtext = `
          <p><strong>Explore STIs, urethritis, and other causes of symptoms, to rule out conditions that present in similar ways to uncomplicated UTI.</strong></p>
          <p>Clinicians should also refer to local guidance where applicable. </p>
        `;
      } else {
        this.result.text = "N/A";
        this.result.subtext = "";
      }
    },
    copyToClipboard() {
      var range;
      var finalText = "";
      if (document.selection) { // IE
        range = document.body.createTextRange();
        range.moveToElementText(document.getElementById('calculator-copy'));
        range.select();
        finalText = this.getDateToday() + "\n\nSymptoms assessment: LUTI in women <65 years\n\n" + range + "\n\nEnter action taken as a result of this alert";
        document.selection.empty();
      } else if (window.getSelection) {
        range = document.createRange();
        range.selectNode(document.getElementById('calculator-copy'));
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        finalText = this.getDateToday() + "\n\nSymptoms assessment: LUTI in women <65 years\n\n" + window.getSelection().toString() + "\n\nEnter action taken as a result of this alert";
        window.getSelection().removeAllRanges();
      }
      var dummy = document.createElement("textarea");
      // Add it to the document
      document.body.appendChild(dummy);

      // Set its ID
      dummy.setAttribute("id", "dummy_id");

      // Output the array into it
      document.getElementById("dummy_id").value = finalText;

      // Select it
      dummy.select();

      // Copy its contents
      document.execCommand("copy");

      // Remove it as its not needed anymore
      document.body.removeChild(dummy);

      var clicked = false;
      var copyButton = document.getElementById('copyButton');
      var last = copyButton.innerHTML;
      if (!clicked) {
            copyButton.innerHTML = 'Copied!';
            clicked = true;
            setTimeout(function () {
                copyButton.innerHTML = last;
                clicked = false;
            }.bind(copyButton), 3000);
        }
    },
    getDateToday() {
      var date = "";
      var today = new Date();
      date = today.getDate() + "/" + (today.getMonth() + 1) + "/" + today.getFullYear() + " " + (today.getHours() < 10 ? "0" + today.getHours() : today.getHours()) + ":" + (today.getMinutes() < 10 ? "0" + today.getMinutes() : today.getMinutes());
      return date;
    },
    anchorLink(i) {
      var element = document.getElementById(i);
      element.scrollIntoView();
    },		
    closedclicked() {
      this.displayresult = false;
    },
    totalYesAnswered(arr) {
      if(!Array.isArray(arr)) return;
      return arr.reduce((a, v)=>a + v);
    },

    optionclicked(qid, oid) {      
      this.displayresult = false;      
      this.showDipstick = false;
      let _score = 0;
      this.questions.forEach((q, index) => {
        if (q.id === qid) {
          q.options.forEach(o => {
            if (o.id === oid) {
              if(o.selected)
              {
                o.selected = false;
                this.questionsAnswered[index] = null;
              }
              else
              {
                o.selected = true;
                this.questionsAnswered[index] = o.value;
              }
            } else {
              o.selected = false;
            }
          });
        }
      });
      let _cnt = 0;
      let _required_cnt = 0;
      let _required_answered_cnt = 0;

      this.questions.forEach(q => {
        if (q.required) {
          _required_cnt++;
        }
      });
      this.questions.forEach(q => {
        q.options.forEach(o => {
          if (o.selected) {
            _score += o.value;
            _cnt++;
          }
          if (o.selected && q.required) {
            _required_answered_cnt++;
          }
        });
      });

      if(this.totalYesAnswered(this.questionsAnswered) > 0){
        this.showAdditonalQuestions = true;
        this.isAdditionalvalid = false;
      } else {
        this.showAdditonalQuestions = false;
        this.isAdditionalvalid = true;
        this.additionalQuestionsAnswered = [null, null];
        this.additonalQuestions.forEach(q => {
          q.options.forEach(o => {
              o.selected = false;
            });
        });
      }

      this.answered_count = _cnt;
      this.isvalid = _required_answered_cnt === _required_cnt;
      if (this.isvalid && this.isAdditionalvalid) {
        this.errormessage = "";
      }
      this.result.score = _score;
      
      let _calctext = "";
      this.questions.forEach(q => {
        q.options.forEach(o => {
          if (o.selected) {
              _calctext = _calctext + "<strong>" + q.title + "</strong><br>"+ o.text +"<br><br>";
          }
        });
      });      
      this.result.calctext = _calctext;
    },

    optionAdditionalclicked(qid, oid) {         
      this.showDipstick = false; 
      this.displayresult = false;
      let _addscore = 0;
      this.additonalQuestions.forEach((q, index) => {
        if (q.id === qid) {
          q.options.forEach(o => {
            if (o.id === oid) {
              if(o.selected)
              {
                o.selected = false;
                this.additionalQuestionsAnswered[index] = null;
              }
              else
              {
                o.selected = true;
                this.additionalQuestionsAnswered[index] = o.value;
              }
            } else {
              o.selected = false;
            }
          });
        }
      });
      
      let _addcnt = 0;
      let _required_addcnt = 0;
      let _required_answered_addcnt = 0;
      
      this.additonalQuestions.forEach(q => {
        if (q.required) {
          _required_addcnt++;
        }
      });
      this.additonalQuestions.forEach(q => {
        q.options.forEach(o => {
          if (o.selected) {
            _addscore += o.value;
            _addcnt++;
          }
          if (o.selected && q.required) {
            _required_answered_addcnt++;
          }
        });
      });
      
      this.add_answered_count = _addcnt;
      this.isAdditionalvalid = _required_answered_addcnt === _required_addcnt ;

      if (this.isvalid && this.isAdditionalvalid) {
        this.errormessage = "";
      }
      
      this.result.score = _addscore;
      let _vagtext = "";
      this.additonalQuestions.forEach(q => {
        q.options.forEach(o => {
          if (o.selected) {
              _vagtext = _vagtext + "<strong>" + q.title + "</strong><br>"+ o.text +"<br><br>";
          }
        });
      });      
      this.result.vagtext = _vagtext;
    }

  }
  };
</script>